import { useEffect, useState } from "react";
import { Building, Calendar, Users, CheckSquare, Square } from "lucide-react";
import axiosInstance from "../../../../axiosInstance";
import { toast } from "react-toastify";
import ButtonWithIcon from "../../../../components/ButtonwithIcon/ButtonWithIcon";
import ColoredCheckbox from "../../../../components/ColoredCheckbox/ColoredCheckbox";
import LoadingScreen from "../../../../components/LoadingScreen/LoadingScreen";

const WorkingDaysSettings = ({ editingData, onCancel, onSuccess }) => {
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState({});
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedSaturdays, setSelectedSaturdays] = useState([]);
  const [selectedSundays, setSelectedSundays] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [showCustomDays, setShowCustomDays] = useState(false);
  const [workingTypeName, setWorkingTypeName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true);
        // Using the new single endpoint that provides all the data
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/branches/enabled/list-with-departments`
        );

        if (response.status === 200) {
          const branchesData = response.data?.data || [];

          if (Array.isArray(branchesData)) {
            setBranches(branchesData);
          } else {
            console.error(
              "Unexpected structure for branches data:",
              branchesData
            );
            setBranches([]);
          }
        }
      } catch (err) {
        console.error("Error fetching branches:", err);
        toast.error("Failed to load branches. Please try again.");
        setBranches([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
  }, []);

  // Fixed useEffect for handling editing data in WorkingDaysSettings
  useEffect(() => {
    if (editingData) {
      // Validate editingData structure before using it
      const safeEditingData = {
        workingTypeName: editingData.workingTypeName || "",
        selectedBranches: Array.isArray(editingData.selectedBranches)
          ? editingData.selectedBranches
          : [],
        selectedDepartments:
          editingData.selectedDepartments &&
          typeof editingData.selectedDepartments === "object"
            ? editingData.selectedDepartments
            : {},
        selectedDays: Array.isArray(editingData.selectedDays)
          ? editingData.selectedDays
          : [],
        selectedSaturdays: Array.isArray(editingData.selectedSaturdays)
          ? editingData.selectedSaturdays
          : [],
        selectedSundays: Array.isArray(editingData.selectedSundays)
          ? editingData.selectedSundays
          : [],
      };

      setWorkingTypeName(safeEditingData.workingTypeName);
      setSelectedBranches(safeEditingData.selectedBranches);
      setSelectedDepartments(safeEditingData.selectedDepartments);
      setSelectedDays(safeEditingData.selectedDays);
      setSelectedSaturdays(safeEditingData.selectedSaturdays);
      setSelectedSundays(safeEditingData.selectedSundays);

      // Set active template based on selected days
      const days = safeEditingData.selectedDays;
      if (
        days.length === 5 &&
        days.includes("Monday") &&
        days.includes("Tuesday") &&
        days.includes("Wednesday") &&
        days.includes("Thursday") &&
        days.includes("Friday")
      ) {
        setActiveTemplate("Mon - Fri");
      } else if (
        days.length === 6 &&
        days.includes("Monday") &&
        days.includes("Tuesday") &&
        days.includes("Wednesday") &&
        days.includes("Thursday") &&
        days.includes("Friday") &&
        days.includes("Saturday")
      ) {
        setActiveTemplate("Mon - Sat");
      } else if (days.length > 0) {
        setActiveTemplate("Custom");
        setShowCustomDays(true);
      }
    }
  }, [editingData]);

  const handleBranchSelect = (branchId) => {
    const isCurrentlySelected = selectedBranches.includes(branchId);

    // If trying to deselect and it's the last selected branch, prevent it
    if (isCurrentlySelected && selectedBranches.length === 1) {
      toast.warning("At least one branch must be selected.");
      return;
    }

    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );

    // Clear departments when deselecting a branch
    if (isCurrentlySelected) {
      setSelectedDepartments((prev) => {
        const newState = { ...prev };
        delete newState[branchId];
        return newState;
      });
    }
  };

  const handleSelectAllBranches = () => {
    const allBranchIds = branches.map((branch) => branch.branchId);
    const allSelected = allBranchIds.every((id) =>
      selectedBranches.includes(id)
    );

    if (allSelected) {
      // Don't allow deselecting all branches
      toast.warning("At least one branch must be selected.");
      return;
    } else {
      // Select all
      setSelectedBranches(allBranchIds);
    }
  };

  // Enhanced submit validation with more detailed error messages
  const handleSubmit = async () => {
    // Validate shift name
    if (!workingTypeName.trim()) {
      toast.error("Please enter a shift name!");
      return;
    }

    // Validate branch selection
    if (selectedBranches.length === 0) {
      toast.error("Please select at least one branch.");
      return;
    }

    // Validate that at least one department is selected across all branches
    const totalSelectedDepartments =
      Object.values(selectedDepartments).flat().length;
    if (totalSelectedDepartments === 0) {
      toast.warning(
        "Please select at least one department from the selected branches."
      );
      return;
    }

    // Validate working days selection
    if (selectedDays.length === 0) {
      toast.error("Please select at least one working day.");
      return;
    }

    setSubmitting(true);

    try {
      const getDayStatus = (day) => selectedDays.includes(day);

      const makeWeekendOff = (type, week) => {
        const list = type === "Saturday" ? selectedSaturdays : selectedSundays;
        return list.includes(week);
      };

      const requestBody = {
        branchIds: selectedBranches.length > 0 ? selectedBranches : [0],
        departmentIds:
          Object.values(selectedDepartments).flat().length > 0
            ? Object.values(selectedDepartments).flat()
            : [0],
        workingDaysId: editingData?.workingDayId || 0,
        workingTypeName: workingTypeName.trim(),
        monday: getDayStatus("Monday"),
        tuesday: getDayStatus("Tuesday"),
        wednesday: getDayStatus("Wednesday"),
        thursday: getDayStatus("Thursday"),
        friday: getDayStatus("Friday"),
        saturday: getDayStatus("Saturday"),
        sunday: getDayStatus("Sunday"),
        leaveOnFirstSaturday: makeWeekendOff("Saturday", 1),
        leaveOnSecondSaturday: makeWeekendOff("Saturday", 2),
        leaveOnThirdSaturday: makeWeekendOff("Saturday", 3),
        leaveOnFourthSaturday: makeWeekendOff("Saturday", 4),
        leaveOnFifthSaturday: makeWeekendOff("Saturday", 5),
        leaveOnFirstSunday: makeWeekendOff("Sunday", 1),
        leaveOnSecondSunday: makeWeekendOff("Sunday", 2),
        leaveOnThirdSunday: makeWeekendOff("Sunday", 3),
        leaveOnFourthSunday: makeWeekendOff("Sunday", 4),
        leaveOnFifthSunday: makeWeekendOff("Sunday", 5),
        companyId: parseInt(process.env.REACT_APP_COMPANY_ID) || 0,
      };

      const isEdit = editingData?.workingDayId;

      isEdit
        ? await axiosInstance.put(
            `${process.env.REACT_APP_API_BASE_URL}/workingdays`,
            requestBody
          )
        : await axiosInstance.post(
            `${process.env.REACT_APP_API_BASE_URL}/workingdays`,
            requestBody
          );

      toast.success(isEdit ? "Updated successfully!" : "Added successfully!");
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.status === 409
          ? err?.response?.data?.message
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDepartment = (branchId, departmentId) => {
    setSelectedDepartments((prev) => {
      const branchDepartments = prev[branchId] || [];
      const isSelected = branchDepartments.includes(departmentId);

      if (isSelected) {
        // Remove the department
        return {
          ...prev,
          [branchId]: branchDepartments.filter((id) => id !== departmentId),
        };
      } else {
        // Add the department only if it's not already there (safety check)
        const newDepartments = branchDepartments.includes(departmentId)
          ? branchDepartments
          : [...branchDepartments, departmentId];

        return {
          ...prev,
          [branchId]: newDepartments,
        };
      }
    });
  };

  const handleSelectAllDepartments = (branchId) => {
    const branch = branches.find((b) => b.branchId === branchId);
    const allDepartmentIds = branch.departments.map(
      (dept) => dept.departmentId
    );
    const currentSelected = selectedDepartments[branchId] || [];
    const allSelected = allDepartmentIds.every((id) =>
      currentSelected.includes(id)
    );

    if (allSelected) {
      // Deselect all departments for this branch
      setSelectedDepartments((prev) => ({
        ...prev,
        [branchId]: [],
      }));
    } else {
      // Select all departments for this branch
      setSelectedDepartments((prev) => ({
        ...prev,
        [branchId]: allDepartmentIds,
      }));
    }
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleWeekend = (type, week) => {
    const setter =
      type === "saturday" ? setSelectedSaturdays : setSelectedSundays;
    setter((prev) =>
      prev.includes(week) ? prev.filter((w) => w !== week) : [...prev, week]
    );
  };

  const handleTemplateSelection = (template) => {
    setActiveTemplate(template);
    setShowCustomDays(false);

    switch (template) {
      case "Mon - Fri":
        setSelectedDays([
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
        ]);
        setSelectedSaturdays([]);
        setSelectedSundays([]);
        break;
      case "Mon - Sat":
        setSelectedDays([
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ]);
        setSelectedSaturdays([]);
        setSelectedSundays([]);
        break;
      case "Custom":
        setSelectedDays([]);
        setSelectedSaturdays([]);
        setSelectedSundays([]);
        setShowCustomDays(true);
        break;
      default:
        break;
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const allBranchIds = branches.map((branch) => branch.branchId);
  const allBranchesSelected =
    allBranchIds.length > 0 &&
    allBranchIds.every((id) => selectedBranches.includes(id));

  const renderBranchesSection = () => {
    if (branches.length === 0) {
      return (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-300">
          <Building className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Active Branches
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            There are no active branches available at the moment. Please check
            back later or contact your administrator.
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {branches.map((branch) => (
          <div
            key={branch.branchId}
            className="p-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm text-gray-900 truncate pr-2">
                {branch.branchName}
              </span>
              <button
                type="button"
                onClick={() => handleBranchSelect(branch.branchId)}
                className={`p-1 rounded-md transition-colors ${
                  selectedBranches.includes(branch.branchId)
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {selectedBranches.includes(branch.branchId) ? (
                  <CheckSquare className="h-4 w-4" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
              <Users className="h-3 w-3" />
              <span>{branch.departments?.length || 0} departments</span>
            </div>

            {/* Display existing office timings for this branch */}
            {branch.officeTimings && branch.officeTimings.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <div className="text-xs text-gray-500 mb-1">
                  Current shifts:
                </div>
                <div className="space-y-1">
                  {branch.officeTimings.slice(0, 2).map((timing) => (
                    <div
                      key={timing.officeTimingId}
                      className="text-xs bg-gray-50 px-2 py-1 rounded"
                    >
                      <div className="font-medium truncate">
                        {timing.shiftName}
                      </div>
                      <div className="text-gray-600 truncate">
                        {timing.shiftForm} - {timing.shiftTo}
                      </div>
                    </div>
                  ))}
                  {branch.officeTimings.length > 2 && (
                    <div className="text-xs text-gray-500 italic">
                      +{branch.officeTimings.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full p-8 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
      {/* Header */}
      <div className="border-b border-gray-100 pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {editingData
            ? "Edit Working Days Configuration"
            : "Working Days Configuration"}
        </h1>
        <p className="text-gray-600 text-lg">
          {editingData
            ? "Update working days for your branches and departments"
            : "Set up working days for your branches and departments"}
        </p>
      </div>

      {/* Configuration Name */}
      <div className="space-y-3 mt-2">
        <label className="block text-sm font-semibold text-gray-700 tracking-wide">
          Shift Name
        </label>
        <input
          type="text"
          value={workingTypeName}
          onChange={(e) => setWorkingTypeName(e.target.value)}
          placeholder="E.g: Regular Week"
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
        />
      </div>

      {/* Branch Selection */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Select Branches
            </h2>
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-2.5 rounded-full">
              {selectedBranches.length} of {branches.length} selected
            </span>
          </div>
          {branches.length > 0 && (
            <button
              onClick={handleSelectAllBranches}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-[20px] transition-colors duration-200"
            >
              {allBranchesSelected ? (
                <>
                  <CheckSquare className="h-4 w-4" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="h-4 w-4" />
                  Select All
                </>
              )}
            </button>
          )}
        </div>

        {renderBranchesSection()}
      </div>

      {/* Department Selection */}
      {selectedBranches.length > 0 && (
        <div className="space-y-6">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Select Departments
              </h2>
            </div>

            <div className="flex flex-wrap gap-6">
              {selectedBranches.map((branchId) => {
                const branch = branches.find((b) => b.branchId === branchId);

                // Safety check - ensure branch exists and has departments array
                if (!branch || !Array.isArray(branch.departments)) {
                  return null;
                }

                const selectedDepts = selectedDepartments[branchId] || [];
                const allDeptIds = branch.departments
                  .filter((dept) => dept && dept.departmentId) // Filter out null/undefined departments
                  .map((dept) => dept.departmentId);

                const allDepartmentsSelected =
                  allDeptIds.length > 0 &&
                  allDeptIds.every((id) => selectedDepts.includes(id));

                return (
                  <div
                    key={branchId}
                    className="flex-1 min-w-[300px] max-w-sm space-y-3 border border-gray-200 p-4 rounded-xl"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800 text-base">
                        {branch.branchName}
                      </h3>
                      {branch.departments.length > 0 && (
                        <button
                          onClick={() => handleSelectAllDepartments(branchId)}
                          className="text-xs text-green-600 hover:underline"
                        >
                          {allDepartmentsSelected
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      {branch.departments
                        .filter(
                          (department) =>
                            department &&
                            department.departmentId &&
                            department.departmentName
                        ) // Filter out invalid departments
                        .map((department) => (
                          <label
                            key={department.departmentId}
                            className="flex items-center gap-2 text-sm text-gray-700"
                          >
                            <ColoredCheckbox
                              name={`dept-${branch.branchId}-${department.departmentId}`}
                              color="green"
                              checked={selectedDepartments[
                                branch.branchId
                              ]?.includes(department.departmentId)}
                              onChange={() =>
                                toggleDepartment(
                                  branch.branchId,
                                  department.departmentId
                                )
                              }
                            />
                            <span className="truncate">
                              {department.departmentName}
                            </span>
                          </label>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Working Days Selection */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Calendar className="h-6 w-6 text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-900">Working Days</h2>
        </div>

        <div className="flex flex-wrap gap-3">
          {["Mon - Fri", "Mon - Sat", "Custom"].map((template) => (
            <button
              key={template}
              onClick={() => handleTemplateSelection(template)}
              className={`px-6 py-3 rounded-[20px] border-2 transition-all duration-200 font-medium ${
                activeTemplate === template
                  ? "text-white shadow-lg"
                  : "border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700"
              }`}
              style={
                activeTemplate === template
                  ? {
                      borderColor: "var(--active-color)",
                      backgroundColor: "var(--active-color)",
                    }
                  : {}
              }
            >
              {template}
            </button>
          ))}
        </div>

        {showCustomDays && (
          <div className="rounded-xl p-2 border border-purple-200">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {[
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ].map((day) => (
                <label
                  key={day}
                  className="flex items-center gap-2 p-1 bg-white rounded-[20px] cursor-pointer transition-all duration-200 hover:shadow-sm"
                >
                  <ColoredCheckbox
                    name={`day-${day}`}
                    color="green"
                    checked={selectedDays.includes(day)}
                    onChange={() => toggleDay(day)}
                    label={day}
                  />
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Weekend Off Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {["Saturday", "Sunday"].map((dayType) => (
          <div key={dayType} className="space-y-4">
            <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-600" />
              {dayType}s Off
            </h3>
            <div className="rounded-xl p-1">
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5].map((week) => (
                  <label
                    key={week}
                    className="flex items-center gap-2 p-3 bg-white rounded-[20px] border border-gray-100 hover:bg-gray-50 cursor-pointer transition-all duration-200 hover:shadow-sm"
                  >
                    <ColoredCheckbox
                      name={`${dayType}-${week}`}
                      color="green"
                      checked={
                        dayType === "Saturday"
                          ? selectedSaturdays.includes(week)
                          : selectedSundays.includes(week)
                      }
                      onChange={() =>
                        toggleWeekend(dayType.toLowerCase(), week)
                      }
                    />

                    <span className="text-sm text-gray-700 font-medium">
                      Week {week}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Submit Button */}
      <div className="flex gap-4 pt-6 border-t border-gray-200">
        {onCancel && (
          <ButtonWithIcon
            onClick={handleCancel}
            icon="lucide:x"
            text="Cancel"
            className="cancel-button"
          />
        )}

        <ButtonWithIcon
          onClick={handleSubmit}
          disabled={submitting}
          icon={submitting ? "lucide:loader-2" : "lucide:check"}
          text={
            submitting
              ? "Saving..."
              : editingData
              ? "Update Configuration"
              : "Save Configuration"
          }
          className="submit-button"
        />
      </div>
    </div>
  );
};

export default WorkingDaysSettings;
